"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { formatRangeP } from "@/components/tools/stats-tests-more/range-p";
import type { TUKEY_HSD_CALCULATOR_STRINGS } from "@/components/tools/tukey-hsd-calculator";
import { t } from "@/lib/i18n/interpolate";
import type { CitationParts } from "@/lib/tool-kit/citation-parts";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type { TukeyResult } from "@/lib/tool-kit/stats-tests-more/tukey-hsd";

/** Results for the Tukey HSD calculator. */

type Strings = typeof TUKEY_HSD_CALCULATOR_STRINGS;

function apaPairs(
  r: TukeyResult,
  names: readonly string[],
  strings: Strings,
  fmt: NumberFormatter
): CitationParts {
  const parts: CitationParts = [];
  const level = fmt((1 - r.alpha) * 100, { maximumFractionDigits: 1 });
  for (const [index, pair] of r.pairs.entries()) {
    if (index > 0) {
      parts.push({ text: "; " });
    }
    parts.push(
      ...richTemplate(strings.apaPairTemplate, {
        pair: t(strings.versus, {
          a: names[pair.first],
          b: names[pair.second],
        }),
        diff: fixed(pair.difference, 2, fmt),
        level,
        lo: fixed(pair.lower, 2, fmt),
        hi: fixed(pair.upper, 2, fmt),
        p: apaP(pair.p, fmt),
      })
    );
  }
  return parts;
}

export function TukeyHsdResults({
  r,
  names,
  strings,
  fmt,
}: {
  r: TukeyResult;
  names: readonly string[];
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const a = r.anova;
  const significantCount = r.pairs.filter((pair) => pair.p < r.alpha).length;
  const levelText = fmt(1 - r.alpha, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const omnibus = richTemplate(strings.apaOmnibusTemplate, {
    df1: fmt(a.df1),
    df2: fmt(a.df2),
    f: fixed(a.f, 2, fmt),
    p: apaP(a.p, fmt),
  });
  // The pair the working walks through: the one with the largest |q|.
  const example = r.pairs.reduce((best, pair) =>
    pair.q > best.q ? pair : best
  );
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.qCritical} value={n(r.qCritical)} />
        <StatCard
          label={strings.hsd}
          value={r.hsd === null ? strings.hsdNone : n(r.hsd)}
        />
        <StatCard label={strings.anovaF} value={n(a.f)} />
        <StatCard emphasis label={strings.anovaP} value={formatP(a.p, fmt)} />
        <StatCard label={strings.dfWithin} value={fmt(a.df2)} />
        <StatCard label={strings.msWithin} value={n(a.msWithin)} />
      </StatGrid>
      <Decision reject={significantCount > 0}>
        {t(significantCount > 0 ? strings.summaryReject : strings.summaryNone, {
          alpha: fmt(r.alpha),
          count: fmt(significantCount),
          total: fmt(r.pairs.length),
        })}
      </Decision>
      {r.k === 2 ? <p className={HINT}>{strings.twoGroups}</p> : null}
      <h3 className={WIDGET_H3}>{strings.groupsHeading}</h3>
      <p className={HINT}>{strings.groupsHint}</p>
      <ResultTable
        head={[
          strings.group,
          strings.n,
          strings.mean,
          strings.sd,
          strings.letters,
        ]}
        rows={a.groups.map((group, i) => ({
          key: `${i}-${names[i]}`,
          cells: [
            names[i],
            fmt(group.n),
            n(group.mean),
            n(group.sd),
            r.letters[i],
          ],
        }))}
      />
      <h3 className={WIDGET_H3}>{strings.pairsHeading}</h3>
      <p className={HINT}>
        {t(strings.pairsHint, {
          level: levelText,
          q: n(r.qCritical),
          k: fmt(r.k),
          df: fmt(a.df2),
        })}
      </p>
      <ResultTable
        head={[
          strings.comparison,
          strings.difference,
          strings.se,
          strings.q,
          strings.pAdjusted,
          strings.ciLow,
          strings.ciHigh,
          strings.d,
          strings.significant,
        ]}
        rows={r.pairs.map((pair) => ({
          key: `${pair.first}-${pair.second}`,
          cells: [
            t(strings.versus, { a: names[pair.first], b: names[pair.second] }),
            n(pair.difference),
            n(pair.se),
            n(pair.q),
            formatRangeP(pair.p, fmt, strings.pBelow),
            n(pair.lower),
            n(pair.upper),
            n(pair.d),
            pair.p < r.alpha ? strings.yes : strings.no,
          ],
        }))}
      />
      <Working heading={strings.howCalculated}>
        <p>
          {t(strings.workingMs, {
            ss: n(a.ssWithin),
            df: fmt(a.df2),
            ms: n(a.msWithin),
            k: fmt(r.k),
          })}
        </p>
        <p>
          {t(strings.workingSe, {
            a: names[example.first],
            b: names[example.second],
            ms: n(a.msWithin),
            ni: fmt(a.groups[example.first].n),
            nj: fmt(a.groups[example.second].n),
            se: n(example.se),
          })}
        </p>
        <p>
          {t(strings.workingQ, {
            diff: n(example.difference),
            se: n(example.se),
            q: n(example.q),
            p: formatRangeP(example.p, fmt, strings.pBelow),
            k: fmt(r.k),
            df: fmt(a.df2),
          })}
        </p>
        <p>
          {t(strings.workingCrit, {
            q: n(r.qCritical),
            alpha: fmt(r.alpha),
            half: n(r.qCritical * example.se),
          })}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaOmnibusHeading}
        parts={omnibus}
      />
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apaPairs(r, names, strings, fmt)}
      />
    </section>
  );
}
